import { z } from 'zod';

// Projects (desarrollos): structured commercial context per business. The AI receives a bounded rendering
// of the linked project as TRUSTED reference data, distinct from the untrusted transcript.

const Text = (max: number) => z.string().trim().max(max);
const OptText = (max: number) => z.string().trim().max(max).nullable();

export const PROJECT_STATUSES = ['active', 'archived'] as const;
export const ProjectStatusSchema = z.enum(PROJECT_STATUSES);
export type ProjectStatus = z.infer<typeof ProjectStatusSchema>;

export const CURRENCIES = ['MXN', 'USD'] as const;

export const PAYMENT_KINDS = ['cash', 'bank_credit', 'infonavit', 'fovissste', 'developer_financing', 'other'] as const;
export const PaymentKindSchema = z.enum(PAYMENT_KINDS);
export type PaymentKind = z.infer<typeof PaymentKindSchema>;

export const MATERIAL_KINDS = ['brochure', 'video', 'location', 'render', 'spec_sheet', 'pdf', 'link', 'other'] as const;
export const MaterialKindSchema = z.enum(MATERIAL_KINDS);
export type MaterialKind = z.infer<typeof MaterialKindSchema>;

/** Objection catalog (§15). Shared by project answers and Conversation Intelligence findings. */
export const OBJECTION_TYPES = [
  'too_expensive',
  'think_about_it',
  'just_looking',
  'send_information',
  'no_time',
  'location',
  'comparing',
  'consult_partner',
  'no_call',
  'financing',
  'other',
] as const;
export const ObjectionTypeSchema = z.enum(OBJECTION_TYPES);
export type ObjectionType = z.infer<typeof ObjectionTypeSchema>;

const Url = z.string().trim().max(1000).refine((v) => /^https?:\/\/\S+$/i.test(v), 'URL inválida (http/https)');

export const ProjectLocationSchema = z.object({
  address: OptText(300),
  city: OptText(120),
  zone: OptText(120),
  mapsUrl: Url.nullable(),
});

export const PriceRangeSchema = z
  .object({
    label: Text(120),
    minPrice: z.number().nonnegative().max(1e12).nullable(),
    maxPrice: z.number().nonnegative().max(1e12).nullable(),
    currency: z.enum(CURRENCIES),
    notes: OptText(300),
  })
  .refine((p) => p.minPrice === null || p.maxPrice === null || p.minPrice <= p.maxPrice, { message: 'El precio mínimo no puede ser mayor al máximo' });

export const AvailabilitySchema = z.object({
  unitType: Text(120).min(1),
  bedrooms: z.number().int().min(0).max(20).nullable(),
  availableUnits: z.number().int().min(0).max(100_000).nullable(),
  totalUnits: z.number().int().min(0).max(100_000).nullable(),
  notes: OptText(300),
});

export const DeliveryDateSchema = z.object({
  label: Text(120).min(1),
  /** YYYY-MM or YYYY-MM-DD; null when only described in notes. */
  date: z.string().regex(/^\d{4}-\d{2}(-\d{2})?$/, 'Formato AAAA-MM o AAAA-MM-DD').nullable(),
  notes: OptText(300),
});

export const PaymentOptionSchema = z.object({
  kind: PaymentKindSchema,
  name: Text(120).min(1),
  description: OptText(500),
});

export const MaterialSchema = z.object({
  kind: MaterialKindSchema,
  title: Text(160).min(1),
  url: Url,
});

export const QualificationCriterionSchema = z.object({
  label: Text(160).min(1),
  description: OptText(300),
  required: z.boolean(),
});

export const ApprovedObjectionAnswerSchema = z.object({
  type: ObjectionTypeSchema,
  objection: Text(300).min(1),
  approvedAnswer: Text(1500).min(1),
});

export const AI_CONTEXT_MAX_CHARS = 4000;

/** Editable fields of a project. */
export const ProjectInputSchema = z.object({
  name: Text(120).min(1),
  status: ProjectStatusSchema,
  description: OptText(1000),
  location: ProjectLocationSchema,
  priceRanges: z.array(PriceRangeSchema).max(20),
  availability: z.array(AvailabilitySchema).max(30),
  deliveryDates: z.array(DeliveryDateSchema).max(20),
  paymentOptions: z.array(PaymentOptionSchema).max(20),
  amenities: z.array(Text(120).min(1)).max(50),
  materials: z.array(MaterialSchema).max(30),
  qualificationCriteria: z.array(QualificationCriterionSchema).max(20),
  objections: z.array(ApprovedObjectionAnswerSchema).max(30),
  /** Free-form "contexto adicional para IA" (trusted reference text written by the business). */
  aiContext: OptText(AI_CONTEXT_MAX_CHARS),
});
export type ProjectInput = z.infer<typeof ProjectInputSchema>;

export const CreateProjectSchema = ProjectInputSchema.partial().extend({ name: Text(120).min(1) });
export type CreateProject = z.infer<typeof CreateProjectSchema>;
export const UpdateProjectSchema = ProjectInputSchema.partial();
export type UpdateProject = z.infer<typeof UpdateProjectSchema>;

export const ProjectSchema = ProjectInputSchema.extend({
  id: z.string(),
  /** Incremented on every edit; analyses record the project version they used. */
  version: z.number().int(),
  linkedConnections: z.number().int(),
  linkedConversations: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const ProjectSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: ProjectStatusSchema,
  city: z.string().nullable(),
  version: z.number().int(),
  linkedConnections: z.number().int(),
  linkedConversations: z.number().int(),
  updatedAt: z.string(),
});
export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;

export const EMPTY_PROJECT: ProjectInput = {
  name: '',
  status: 'active',
  description: null,
  location: { address: null, city: null, zone: null, mapsUrl: null },
  priceRanges: [],
  availability: [],
  deliveryDates: [],
  paymentOptions: [],
  amenities: [],
  materials: [],
  qualificationCriteria: [],
  objections: [],
  aiContext: null,
};

/** How a conversation got its project. ai_suggested links carry a confidence and never override manual ones. */
export const PROJECT_LINK_SOURCES = ['manual', 'connection_default', 'ai_suggested'] as const;
export const ProjectLinkSourceSchema = z.enum(PROJECT_LINK_SOURCES);
export type ProjectLinkSource = z.infer<typeof ProjectLinkSourceSchema>;

export const ConversationProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  source: ProjectLinkSourceSchema,
  /** Only for ai_suggested. */
  confidence: z.number().min(0).max(1).nullable(),
});
export type ConversationProject = z.infer<typeof ConversationProjectSchema>;

export const LinkConversationProjectSchema = z.object({ projectId: z.string().min(1).max(64).nullable() });
export type LinkConversationProject = z.infer<typeof LinkConversationProjectSchema>;

// ───────── Labels ─────────

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = { active: 'Activo', archived: 'Archivado' };

export const PAYMENT_KIND_LABELS: Record<PaymentKind, string> = {
  cash: 'Contado',
  bank_credit: 'Crédito bancario',
  infonavit: 'Infonavit',
  fovissste: 'Fovissste',
  developer_financing: 'Financiamiento directo',
  other: 'Otro',
};

export const MATERIAL_KIND_LABELS: Record<MaterialKind, string> = {
  brochure: 'Brochure',
  video: 'Video',
  location: 'Ubicación',
  render: 'Renders',
  spec_sheet: 'Ficha técnica',
  pdf: 'PDF',
  link: 'Link',
  other: 'Otro',
};

export const OBJECTION_TYPE_LABELS: Record<ObjectionType, string> = {
  too_expensive: 'Está caro',
  think_about_it: 'Quiero pensarlo',
  just_looking: 'Solo estoy viendo',
  send_information: 'Mándame información',
  no_time: 'No tengo tiempo',
  location: 'No me convence la ubicación',
  comparing: 'Estoy comparando',
  consult_partner: 'Lo tengo que ver con mi pareja',
  no_call: 'No quiero llamada',
  financing: 'Financiamiento / crédito',
  other: 'Otra',
};

export const PROJECT_LINK_SOURCE_LABELS: Record<ProjectLinkSource, string> = {
  manual: 'Asignado manualmente',
  connection_default: 'Predeterminado del número',
  ai_suggested: 'Sugerido por IA',
};
